import React, { useState } from "react";
import { useRouteMatch, Link } from "react-router-dom";
import "./AppSettings.scss";
import "./headerMenus.scss";
import DsLastSynced from "./DsLastSynced/DsLastSynced";
import useLogger from "../Logger/useLogger";
import useIntegratedDataSources, {
  DatasourceCheckConnectionStatusEnum,
} from "./Integrations/DataSources/useIntegratedDataSources";
import {
  SeemPopover,
  TextButton,
  IconButton,
  ActionsMenu,
} from "@SeemplicityDev/shared";
import useDisplayEOY from "../EndOfYear/useDisplayEOY";
import useHelpMenuItems from "./useHelpMenuItems";
import useUserMenuItems from "./useUserMenuItems";

const AppSettings: React.FC = () => {
  const [lastSyncedOpen, setLastSyncedOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { openEndOfYear, allowEndOfYear } = useDisplayEOY();
  const match = useRouteMatch<{ item: string }>("/:item");
  const { logInfo } = useLogger();
  const { integratedDataSources } = useIntegratedDataSources(false, false, {
    check_connection_statuses: DatasourceCheckConnectionStatusEnum.FAILED,
    enabled: true,
  });
  const { menuItems: userMenuItems, loggedUserLabel } = useUserMenuItems();
  const documentationMenu = useHelpMenuItems();

  const handleUserMenuOpenChange = (isOpen: boolean) => {
    setUserMenuOpen(isOpen);
  };

  const onLastSyncClick = () => {
    if (!lastSyncedOpen) {
      logInfo("Last sync button was clicked");
    }

    setLastSyncedOpen(!lastSyncedOpen);
  };

  return (
    <div className={`selection-menu-wrapper settings-menu`}>
      {!!allowEndOfYear && (
        <div
          className={`settings-menu-option-wrapper eoy-button-container`}
          onClick={openEndOfYear}
        >
          <TextButton
            leftIcon="seem-icon-party"
            label="Year In Review"
            onClick={openEndOfYear}
            className="eoy-button"
            buttonStyle="textLink"
          />
        </div>
      )}
      <div
        className={`settings-menu-item ${
          lastSyncedOpen ? "menu-item-active" : ""
        }`}
      >
        <SeemPopover
          isOpen={lastSyncedOpen}
          placement={"bottom-end"}
          body={[
            <DsLastSynced
              key="last-synced"
              close={() => setLastSyncedOpen(false)}
            />,
          ]}
          onOuterAction={() => setLastSyncedOpen(false)}
        >
          <IconButton
            data-testid={"open-last-synced"}
            wrapperClassName={"last-sync-menu-wrap"}
            isIcon="seem-icon-double-refresh"
            buttonStyle={"invert"}
            type={`button`}
            size={"large"}
            onClick={onLastSyncClick}
            isHighSeverityAlert={
              (integratedDataSources?.datasources?.edges?.length ?? 0) > 0
            }
            withTooltip={{ content: "Last Synced" }}
          />
        </SeemPopover>
      </div>
      <div
        className={`settings-menu-item ${
          match?.params?.item === "settings" ? "menu-item-active" : ""
        }`}
      >
        <Link to={"/settings"}>
          <IconButton
            isIcon={"seem-icon-settings"}
            data-testid={"settings-menu-button"}
            className={`settings-menu-option`}
            buttonStyle={"invert"}
            type={`button`}
            size={"large"}
            withTooltip={{ content: "Settings" }}
          />
        </Link>
        <div className="menu-underline" />
      </div>
      <div className={"settings-menu-item"}>
        <ActionsMenu
          menuItems={documentationMenu}
          data-testid={"documentation-menu"}
        >
          <IconButton
            isIcon={"seem-icon-help"}
            data-testid={"doc-link-menu-button"}
            buttonStyle={"invert"}
            size={"large"}
            withTooltip={{ content: "Help" }}
          />
          <div className="menu-underline" />
        </ActionsMenu>
      </div>
      {userMenuItems?.length > 0 && (
        <div className={"settings-menu-item"}>
          <ActionsMenu
            menuItems={userMenuItems}
            className={"app-settings-user-menu"}
            data-testid={"app-settings-user-menu"}
            onMenuOpenChange={handleUserMenuOpenChange}
            place="bottom-end"
          >
            <div
              className={`user-details-container settings-menu-option settings-menu-option-user ${
                userMenuOpen ? "menu-item-active" : ""
              }`}
            >
              <i className="user-icon seem-icon seem-icon-user" />
              <div className={`username-display`}>{loggedUserLabel}</div>
            </div>
          </ActionsMenu>
        </div>
      )}
    </div>
  );
};

export default AppSettings;
